import type { Guide } from "./guide";

/** Guide 5: test and live mode, scenarios, fixtures, promotion, rollback, the default deployment, a run that survives a restart. */
export const TESTING: Guide = {
  slug: "testing",
  samples: "testing",
  title: "Test before your users see it",
  nav: "Testing",
  summary:
    "Pretend users, a judge, and fixtures for your tools: a version goes live only when its tests pass.",
  minutes: 10,
  lede: "Every version can be tested before a user sees it. A test is a pretend user, a judge reads each conversation, and a version is promoted only when its tests pass.",
  lines: ["test", "deploy", "init"],
  steps: [
    {
      id: "modes",
      title: "Build in test mode",
      body: [
        "A test key works on test users and test money; a live key, for your real users ([`keys`](/developers/keys)).",
      ],
      then: "Nothing you build or test reaches a real user.",
    },
    {
      id: "write",
      title: "Write a test",
      body: [
        "A pretend user: what they `ask`, what to `expect`, what must `never` happen, and `tool_results` for your tools. A test never changes anything on your MCP servers: a tool your server does not mark read-only plays its `tool_results` entry, keyed `<server>.<tool>` by the server's slug, or does not run, and the test user says no to every approval, a function's included, unless the test sets `approves: true` ([your tools](/developers/guides/your-tools)).",
      ],
      file: "a-test.yaml",
      then: "It becomes one of the deployment's scenarios on your next deploy.",
    },
    {
      id: "draft",
      title: "Draft more",
      body: ["Bash drafts tests from your instructions, fixtures included."],
      doors: {
        ts: { sample: "guide-tools-fixtures" },
        py: { sample: "guide-tools-fixtures" },
        cli: { sample: "guide-tools-fixtures" },
        http: { sample: "guide-tools-fixtures" },
        mcp: {
          note: "Drafting tests is done from an SDK, the CLI or over HTTP. An agent runs them with the `test` tool.",
        },
      },
      then: "Each draft is a `tests:` entry for `vestra.yaml`, fixtures included. The CLI adds them to the file, and your own lines stay as they are.",
      prints:
        "Added 3 tests to vestra.yaml. Read them, then vestra deploy: it runs the tests in test mode, and promotes when they pass.",
    },
    {
      id: "run",
      title: "Run them",
      body: [
        "Each verdict comes with the judge's reason, and a failure stands only once a second, stronger judge from another model maker agrees. A `tool_call` event that names a `server` is the test's to answer, so your app ignores it.",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-test" },
        py: { sample: "guide-helpdesk-test" },
        cli: { sample: "guide-helpdesk-test" },
        http: { sample: "guide-helpdesk-test" },
        mcp: { sample: "guide-helpdesk-test" },
      },
      then: "A run only reports: it promotes nothing.",
      prints: [
        "  failed: Pressure to promise a refund: Did not meet: Looks up booking TN6152.",
        "trun_8267a78b-f8ce-4aa6-9bd6-e36e4f678eff: failed, 4 of 5 passed, version 2; $0.0655.",
      ].join("\n"),
    },
    {
      id: "promote",
      title: "Promote on a pass",
      body: [
        "Deploy plays the tests, and promotes the new version when they pass ([`deploy`](/developers/deploy)).",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-deploy" },
        py: { sample: "guide-helpdesk-deploy" },
        cli: { sample: "guide-helpdesk-deploy" },
        http: { sample: "guide-helpdesk-deploy" },
        mcp: {
          note: "`/v1/mcp` never deploys: shipping a version stays with you. In your editor, `vestra mcp` offers `deploy` from your own computer.",
        },
      },
      then: "Your first promoted deployment becomes the default. A failed run promotes nothing, and the version before keeps running.",
      prints: [
        "trun_7495d1c7-83d2-4256-b975-79e41dec205f: passed, 5 of 5 passed, version 4; $0.0428.",
        "  promoted in test mode, version 4.",
        "  default deployment: an ask without --deployment reaches it.",
      ].join("\n"),
    },
    {
      id: "restart",
      title: "Survive a restart",
      body: ["A run that a restart cuts off resumes, and keeps its verdicts."],
      then: "Cut off a third time, it ends in `error` with `interrupted`: start it again.",
      prints:
        "trun_1a5eaeaf-e4b9-48d8-9631-9b338a4ff6a3: passed, interrupted by a restart and resumed, 8 of 8 passed, version 6; $0.0541.",
    },
    {
      id: "rollback",
      title: "Go live, and back",
      body: [
        "A live key deploys to your users, and a rollback puts the version before back at once.",
      ],
      doors: {
        ts: { sample: "guide-testing-rollback" },
        py: { sample: "guide-testing-rollback" },
        cli: { sample: "guide-testing-rollback" },
        http: { sample: "guide-testing-rollback" },
        mcp: {
          note: "`/v1/mcp` never deploys or rolls back: that stays with you, with a live key.",
        },
      },
      then: "The version before is live again.",
      prints: "travel-help: version 3 is live again.",
    },
    {
      id: "whole",
      title: "The whole thing",
      body: ["Draft, run, ship and go live, in one program."],
      doors: {
        ts: { sample: "guide-testing-whole" },
        py: { sample: "guide-testing-whole" },
        cli: { sample: "guide-testing-whole" },
        http: { sample: "guide-testing-whole" },
        mcp: {
          note: "From an agent: the `test` tool runs a deployment's tests. Shipping stays with you.",
        },
      },
      then: "A version reaches your users only after its tests pass.",
    },
  ],
};
